import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { safeString } from "../../Utils";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const FETCH_URL = "/work_orders/";

export const WorkOrderSelector = ({ showField = true, noLabel, ...props }) => {
  const {
    setFieldValue,
    values,
    onChange,
    errors,
    fields,
    searchQuery: initialSearchQuery,
    project,
    disabled,
    clearAfterSelect = false,
    excludeIds = [],
    multiple = false,
    onMultipleSelect,
  } = props;

  const name = props?.name ?? "work_order_id";
  const label = props?.label ?? "Work Order";

  const extraFilters = props.extraFilters;

  const filters = useMemo(() => {
    const merged = [];
    if (project?.id) merged.push({ name: "PROJECT", value: project.id });
    if (Array.isArray(extraFilters) && extraFilters.length > 0) {
      merged.push(...extraFilters);
    }
    return merged.length ? merged : null;
  }, [project?.id, extraFilters]);

  const excludedIdSet = useMemo(
    () => new Set((excludeIds || []).map((id) => String(id))),
    [excludeIds]
  );

  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(initialSearchQuery || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open);

  const [selected, setSelected] = useState({
    id: null,
    field1: "Ingen valgt",
    field2: null,
  });
  const [pendingIds, setPendingIds] = useState([]);

  useEffect(() => {
    if (props.open) setOpen(true);
  }, [props.open]);

  useEffect(() => {
    const sel = props.selected;
    if (!sel?.id) {
      setSelected({ id: null, field1: "Ingen valgt", field2: null });
      return;
    }
    const title = sel.displayTitle ?? sel.title;
    if (title) {
      setSelected({
        id: sel.id,
        field1: safeString(title, "Ingen valgt"),
        field2: sel?.customer?.displayTitle ?? null,
        syncStatus: sel?.syncStatus,
      });
      return;
    }
    let cancelled = false;
    axios({ method: "get", url: `${FETCH_URL}${sel.id}` })
      .then((res) => {
        if (cancelled || !res.data?.id) return;
        setSelected({
          id: res.data.id,
          field1: safeString(res.data.displayTitle, "Ingen valgt"),
          field2: res.data?.customer?.displayTitle ?? null,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    props.selected?.id,
    props.selected?.displayTitle,
    props.selected?.title,
    props.selected?.customer?.displayTitle,
    props.selected?.syncStatus,
  ]);

  const fetchItems = useCallback(
    async (searchQuery) => {
      setLoading(true);
      try {
        const response = await axios({
          method: "get",
          url: FETCH_URL,
          params: {
            query: searchQuery || "",
            page: 1,
            sortBy: "created_at",
            sort: "desc",
            filters,
            view: null,
            fields: fields ?? "*",
          },
        });

        const array = [
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ];
        if (response.data?.data?.length > 0) {
          response.data.data.forEach((row) => {
            if (excludedIdSet.has(String(row.id))) return;
            array.push({
              id: row.id,
              value: row.id,
              primary: safeString(row?.displayTitle, "Ingen valgt"),
              secondary: row?.customer?.displayTitle ?? null,
              object: row,
            });
          });
        }
        setItems(array);
      } catch {
        setItems([
          { id: null, value: null, primary: "Ingen valgt", secondary: null },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [filters, fields, extraFilters, excludedIdSet]
  );

  const debouncedFetch = useCallback(
    debounce((q) => fetchItems(q), 300),
    [fetchItems]
  );

  useEffect(() => {
    if (!open) return;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  const handleOpen = () => {
    if (disabled) return;
    if (initialSearchQuery) setQuery(initialSearchQuery);
    if (multiple) setPendingIds([]);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
  };

  const applySelection = (id, item) => {
    if (values && setFieldValue) setFieldValue(name, id);
    if (onChange) {
      onChange({
        target: {
          name,
          value: id,
          object: item,
          data: item,
        },
      });
    }
  };

  const handleSelect = (item) => {
    if (multiple) {
      if (item.id == null) return;
      setPendingIds((prev) => {
        const key = String(item.id);
        return prev.some((id) => String(id) === key)
          ? prev.filter((id) => String(id) !== key)
          : [...prev, item.id];
      });
      return;
    }

    if (item.id != null) {
      setSelected({
        id: item.id,
        field1: safeString(item.primary, "Ingen valgt"),
        field2: item.secondary ?? null,
      });
      applySelection(item.id, item.object ?? item);
    } else {
      setSelected({ id: null, field1: "Ingen valgt", field2: null });
      applySelection(null, null);
    }
    if (clearAfterSelect && item.id != null) {
      setSelected({ id: null, field1: "Ingen valgt", field2: null });
    }
    handleClose();
  };

  const pendingItems = useMemo(
    () =>
      items.filter(
        (item) => item.id != null && pendingIds.some((id) => String(id) === String(item.id))
      ),
    [items, pendingIds]
  );

  const handleConfirmMultiple = () => {
    const selectedObjects = pendingItems.map((item) => item.object ?? item).filter((row) => row?.id);
    if (onMultipleSelect) {
      onMultipleSelect(selectedObjects);
    } else if (onChange) {
      onChange({ target: { name, objects: selectedObjects } });
    }
    setPendingIds([]);
    handleClose();
  };

  const hasSelection = multiple ? false : selected?.id != null;
  const triggerLabel = multiple
    ? t(props.triggerLabelKey || "project.event.select_work_orders")
    : selected.field1;

  const triggerButton = showField ? (
    <div>
      {noLabel !== false && (
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {props?.labelAction?.action ? (
          <button
            type="button"
            onClick={props.labelAction.action}
            className="text-blue-500 hover:text-blue-700"
          >
            {t(label)}
          </button>
        ) : hasSelection ? (
          <Link
            to={
              props.labelAction?.path ??
              `/dashboard/work_orders/${selected.id}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700"
          >
            {t(label)}
          </Link>
        ) : (
          <span>{t(label)}</span>
        )}
      </label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={[
          "relative w-full flex items-center justify-between",
          "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
          "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
          disabled
            ? "cursor-not-allowed border-gray-200 text-gray-400"
            : hasSelection
              ? "border-blue-400 text-gray-900 hover:border-blue-500"
              : "border-gray-300 text-gray-500 hover:border-gray-400",
        ].join(" ")}
      >
        <span className="truncate">{triggerLabel}</span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-gray-400 ml-2" />
      </button>
      {errors?.[name] && (
        <p className="mt-1 text-xs text-red-600">{errors[name]}</p>
      )}
    </div>
  ) : null;

  const dialogContent = (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      <div className="flex-shrink-0 p-3 sm:p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">{t(label)}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Search work orders...")}
            autoFocus
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
        {loading ? (
          <SelectorListSkeleton />
        ) : (
          <ul className="divide-y divide-gray-100">
            {items
              .filter((item) => !(multiple && item.id == null))
              .map((item) => {
              const isSelected = multiple
                ? pendingIds.some((id) => String(id) === String(item.id))
                : item.id === selected?.id;
              return (
                <li
                  key={item.id ?? "none"}
                  onClick={() => handleSelect(item)}
                  className={[
                    "flex items-center justify-between px-4 py-3 cursor-pointer transition-colors",
                    isSelected
                      ? "bg-blue-50 text-blue-700"
                      : "hover:bg-gray-50 text-gray-900",
                  ].join(" ")}
                >
                  <div className="min-w-0 flex items-center gap-3">
                    {multiple ? (
                      <input
                        type="checkbox"
                        readOnly
                        checked={isSelected}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                    ) : null}
                    <div>
                      <p className="text-sm font-medium truncate">{item.primary}</p>
                      {item.secondary && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {item.secondary}
                        </p>
                      )}
                    </div>
                  </div>
                  {!multiple && isSelected && (
                    <CheckIcon className="h-4 w-4 flex-shrink-0 ml-3 text-blue-600" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {multiple ? (
        <div className="flex-shrink-0 border-t border-gray-200 p-3 sm:p-4 flex items-center justify-between gap-3">
          <span className="text-sm text-gray-600">
            {t("project.plan.multi_select_selected", { count: pendingIds.length })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              disabled={pendingIds.length === 0}
              onClick={handleConfirmMultiple}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {t("project.plan.add_selected", { count: pendingIds.length })}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {triggerButton}

      <PMDialog
        open={open}
        onClose={handleClose}
        size={multiple ? "lg" : "md"}
        dense
        zIndex={3000}
        closeOnOutsideClick
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};
